import React from "react";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Dumbbell, Award, CalendarIcon, Clock, Activity } from "lucide-react";
import { cn } from "../utils/cn";
import { WorkoutHistoryItem } from "../services/workout";

interface SelectedDayWorkoutsProps {
  selectedDate: Date | null;
  selectedDayWorkouts: WorkoutHistoryItem[];
  navigate: (url: string) => void;
  formatDuration: (seconds?: number) => string;
}

export const SelectedDayWorkouts: React.FC<SelectedDayWorkoutsProps> = ({
  selectedDate,
  selectedDayWorkouts,
  navigate,
  formatDuration,
}) => {
  if (!selectedDate) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="mt-6"
    >
      <h3 className="text-xl font-semibold mb-4 flex items-center">
        <CalendarIcon className="mr-2 h-5 w-5 text-primary" />
        Workouts on {format(selectedDate, "MMMM d, yyyy")}
      </h3>
      {selectedDayWorkouts.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <div className="flex flex-col items-center justify-center text-muted-foreground">
              <Dumbbell className="h-12 w-12 mb-4 opacity-20" />
              <p className="mb-4">No workouts on this day</p>
              <Button
                variant="outline"
                onClick={() => navigate("/workouts/new")}
              >
                Create a Workout
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {selectedDayWorkouts.map((workout, index) => (
            <motion.div
              key={workout.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <Card className="overflow-hidden h-full flex flex-col">
                <CardHeader
                  className={cn(
                    "pb-2 border-b",
                    workout.status === "completed"
                      ? "bg-green-50 dark:bg-green-900/10"
                      : workout.status === "in_progress"
                      ? "bg-blue-50 dark:bg-blue-900/10"
                      : "bg-gray-50 dark:bg-gray-800/10"
                  )}
                >
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-lg flex items-center">
                      {/* Status Icon can be added here if passed as prop */}
                      <span className="ml-2">
                        {workout.workout_name}
                      </span>
                    </CardTitle>
                    <Badge
                      variant={
                        workout.status === "completed"
                          ? "default"
                          : workout.status === "in_progress"
                          ? "secondary"
                          : "destructive"
                      }
                      className="ml-2"
                    >
                      {workout.status === "completed"
                        ? "Completed"
                        : workout.status === "in_progress"
                        ? "In Progress"
                        : "Cancelled"}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-4 flex-grow">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span>{format(workout.date, "h:mm a")}</span>
                    </div>
                    {workout.duration && (
                      <div className="flex items-center">
                        <Activity className="h-4 w-4 mr-2 text-muted-foreground" />
                        <span>{formatDuration(workout.duration)}</span>
                      </div>
                    )}
                    <div className="flex items-center col-span-2">
                      <Dumbbell className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span>
                        {workout.exercises_completed} of {workout.total_exercises} exercises completed
                      </span>
                    </div>
                  </div>
                  {workout.status === "completed" && (
                    <div className="mt-4 p-2 bg-green-50 dark:bg-green-900/10 rounded-md flex items-center">
                      <Award className="h-5 w-5 mr-2 text-green-500" />
                      <span className="text-sm font-medium">
                        Workout completed successfully!
                      </span>
                    </div>
                  )}
                </CardContent>
                <CardFooter className="pt-2 pb-3 border-t bg-muted/30">
                  <Button
                    variant="default"
                    size="sm"
                    className="w-full"
                    onClick={() => navigate(`/activity/${workout.id}`)}
                  >
                    View Details
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
};
